import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { ArrowLeft, PackageCheck, Eye, Pencil } from "lucide-react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../components/LoadingOverlay";
import { formatDateTime } from "../../utils/format";

const RequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [exit, setExit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    fetchRequest();

    const user = JSON.parse(localStorage.getItem("user"));

    if (user?.role?.name) {
      setUserRole(user.role.name);
    }
  }, []);

  const fetchRequest = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/inventory-requests/${id}`);

      setRequest(res.data);

      // Si la solicitud ya fue entregada,
      // cargar la salida y sus firmas
      if (res.data.exit_id) {
        try {
          const exitRes = await api.get(`/exits/${res.data.exit_id}`);

          setExit(exitRes.data);
        } catch (error) {
          console.error("Error cargando salida:", error);

          toast.error("No se pudo cargar la información de la entrega.");
        }
      }
    } catch (error) {
      console.error(error);

      toast.error("Error al cargar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold">
            Pendiente
          </span>
        );

      case "delivered":
        return (
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
            Entregada
          </span>
        );

      case "cancelled":
        return (
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
            Cancelada
          </span>
        );

      default:
        return status;
    }
  };

  const canEditNow = () => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    const minute = now.getMinutes();

    return [5, 6, 0, 1, 2].includes(day) ||
      (day === 3 && (hour < 17 || (hour === 17 && minute === 0)));
  };

  if (!request && !loading) {
    return <div className="p-6">Solicitud no encontrada.</div>;
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <LoadingOverlay loading={loading} />

      {request && (
        <>
          {/* HEADER */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-800">
                Solicitud de pedido
              </h2>

              {getStatus(request.status)}
            </div>

            <button
              onClick={() => navigate("/requests")}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={18} />
              Volver
            </button>
          </div>

          {/* INFORMACIÓN */}
          <div className="mb-6 bg-gray-50 p-6 rounded-lg shadow-inner grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                N° Solicitud
              </label>

              <input
                type="text"
                value={request.request_number || "—"}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Área solicitante
              </label>

              <input
                type="text"
                value={request.client?.name || "—"}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Usuario
              </label>

              <input
                type="text"
                value={request.user?.name || "—"}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Fecha solicitud
              </label>

              <input
                type="text"
                value={formatDateTime(request.requested_at)}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Observaciones
              </label>

              <textarea
                value={request.observations || "Sin observaciones"}
                readOnly
                rows={3}
                className="border rounded px-3 py-2 bg-gray-100 resize-none cursor-not-allowed"
              />
            </div>
          </div>
          {/* INFORMACIÓN DE ENTREGA */}

          {request.status === "delivered" && exit && (
            <div className="mb-6 border border-green-200 bg-green-50 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl font-bold text-green-800">
                  Información de entrega
                </h3>

                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                  Entrega realizada
                </span>
              </div>

              {/* DATOS DE LA ENTREGA */}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Fecha y hora */}

                <div className="flex flex-col">
                  <label className="text-sm font-bold text-gray-700 mb-1">
                    Fecha y hora de entrega
                  </label>

                  <input
                    type="text"
                    value={
                      exit.signed_at
                        ? new Date(exit.signed_at).toLocaleString("es-CO", {
                            dateStyle: "short",
                            timeStyle: "medium",
                          })
                        : "—"
                    }
                    readOnly
                    className="border rounded px-3 py-2 bg-white"
                  />
                </div>

                {/* Bodeguero */}

                <div className="flex flex-col">
                  <label className="text-sm font-bold text-gray-700 mb-1">
                    Entregado por
                  </label>

                  <input
                    type="text"
                    value={exit.user?.name || "—"}
                    readOnly
                    className="border rounded px-3 py-2 bg-white"
                  />
                </div>

                {/* Persona que recibe */}

                <div className="flex flex-col">
                  <label className="text-sm font-bold text-gray-700 mb-1">
                    Recibido por
                  </label>

                  <input
                    type="text"
                    value={exit.receiver_name || "—"}
                    readOnly
                    className="border rounded px-3 py-2 bg-white"
                  />
                </div>
              </div>

              {/* FIRMAS */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* FIRMA BODEGUERO */}

                <div className="bg-white border rounded-xl p-4">
                  <h4 className="font-bold text-gray-700 mb-3 text-center">
                    Firma del bodeguero
                  </h4>

                  <div className="border rounded-lg h-48 flex items-center justify-center bg-gray-50">
                    {exit.delivery_signature ? (
                      <img
                        src={exit.delivery_signature}
                        alt="Firma del bodeguero"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400">
                        Sin firma registrada
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 text-center mt-2">
                    Responsable de la entrega
                  </p>
                </div>

                {/* FIRMA RECEPTOR */}

                <div className="bg-white border rounded-xl p-4">
                  <h4 className="font-bold text-gray-700 mb-3 text-center">
                    Firma de quien recibe
                  </h4>

                  <div className="border rounded-lg h-48 flex items-center justify-center bg-gray-50">
                    {exit.receiver_signature ? (
                      <img
                        src={exit.receiver_signature}
                        alt="Firma de quien recibe"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400">
                        Sin firma registrada
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 text-center mt-2">
                    Persona responsable de recibir los insumos
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* PRODUCTOS */}
          <h3 className="text-xl font-semibold mb-4">Productos solicitados</h3>

          <div className="overflow-x-auto rounded-xl shadow">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-center uppercase text-sm">
                  <th className="px-6 py-3">Insumo</th>
                  <th className="px-6 py-3">Presentación</th>
                  <th className="px-6 py-3">Stock al solicitar</th>
                  <th className="px-6 py-3">Cantidad solicitada</th>
                  <th className="px-6 py-3">Entregado</th>
                  <th className="px-6 py-3">Motivo</th>
                </tr>
              </thead>

              <tbody className="text-center">
                {request.request_details?.map((detail) => (
                  <tr key={detail.id} className="border-t">
                    <td className="px-6 py-4 font-medium">
                      {detail.product?.name}
                    </td>

                    <td className="px-6 py-4">
                      {detail.presentation?.name || "-"}
                    </td>

                    <td className="px-6 py-4">{detail.stock_at_request}</td>

                    <td className="px-6 py-4">{detail.requested_quantity}</td>

                    <td className="px-6 py-4">
                      {detail.delivered_quantity || 0}
                    </td>
                    <td className="px-6 py-4">
                      {detail.delivery_reason
                        ? detail.delivery_reason
                        : request.status === "delivered"
                          ? "Completo"
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {request.status === "pending" &&
            (userRole === "Administrador" || userRole === "Insumos") && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => navigate(`/requests/${request.id}/delivery`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg flex items-center gap-2"
                >
                  <PackageCheck size={18} />
                  Preparar entrega
                </button>
              </div>
            )}

          {request.status === "pending" &&
            request.client_id === JSON.parse(localStorage.getItem("user"))?.client_id &&
            canEditNow() && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => navigate(`/requests/${request.id}/edit`)}
                  className="bg-yellow-400 hover:bg-yellow-500 text-white px-5 py-2 rounded-lg flex items-center gap-2"
                >
                  <Pencil size={18} />
                  Editar solicitud
                </button>
              </div>
            )}

          {request.status === "delivered" &&
            request.exit_id &&
            (userRole === "Administrador" || userRole === "Insumos") && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => navigate(`/exits/${request.exit_id}`)}
                  className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg flex items-center gap-2"
                >
                  <PackageCheck size={18} />
                  Ver salida
                </button>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default RequestDetails;
